/* global mainScript */
import React, { useContext } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

// context
import Context from "./Context";

// style rules
import { Animation } from "../../styles";

const MediaCard = ({ mediaItem }) => {
	const {
		data: { selectedFilters },
		actions: { toggleSidePanel },
	} = useContext(Context);

	// themes have 3 states
	// 1. empty theme we display nothing
	// 2. one theme we only display that theme
	// 3. multiple themes attached, we need to display "RelevantTheme & More".
	const renderRelevantThemeTag = () => {
		// State 1
		if (!mediaItem.themes) return null;

		// State 2
		if (mediaItem.themes.length == 1) {
			return (
				<Tag dangerouslySetInnerHTML={{ __html: mediaItem.themes[0].title }} />
			);
		}

		// State 3
		// copy the array of themes attached to media item
		let themesArr = [...mediaItem.themes];

		// compare the array of themes with the array of selected themes
		let relevantThemeIndex = themesArr.findIndex((t) =>
			selectedFilters.some((taxonomyObj) =>
				taxonomyObj.filters.some((f) => f.slug == t.slug)
			)
		);

		// display the tag based on the correct array index
		if (relevantThemeIndex > 0) {
			return (
				<Tag
					upperCase={true}
					dangerouslySetInnerHTML={{
						__html: themesArr[relevantThemeIndex].title + " & More",
					}}
				/>
			);
		}
		// is no relevant tag we just fall back to first entry
		return (
			<Tag
				upperCase={true}
				dangerouslySetInnerHTML={{ __html: themesArr[0].title + " & More" }}
			/>
		);
	};
	return (
		<Card
			initial={"initial"}
			animate={"animate"}
			exit={"initial"}
			variants={Animation.cards.opacityVariants}
			onClick={() => toggleSidePanel(mediaItem)}
		>
			<AspectRatioBox>
				{mediaItem.image ? (
					<img src={mediaItem.image} alt={`Image for ${mediaItem.title}`} />
				) : (
					<img
						src={
							mainScript.assetPath + "images/png/graphics/story-placeholder.png"
						}
						alt={"Card placeholder"}
					/>
				)}
			</AspectRatioBox>
			<Tags>
				{renderRelevantThemeTag()}
				{mediaItem.decade && <Tag>{mediaItem.decade.title}</Tag>}
			</Tags>
			<Title dangerouslySetInnerHTML={{ __html: mediaItem.title }} />
		</Card>
	);
};

export default MediaCard;

MediaCard.propTypes = {
	toggleSidePanel: PropTypes.func,
	mediaItem: PropTypes.object,
};

const Card = styled(motion.li)`
	list-style: none;
	cursor: pointer;

	&:hover {
		img {
			transition: all 0.5s;
			transform: scale(1.1);
		}
	}
`;

const AspectRatioBox = styled.div`
	position: relative;
	width: 100%;
	display: block;
	overflow: hidden;
	padding-top: calc(302 / 302 * 100%);

	> img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center center;
		transition: all 0.5s;
	}
`;

const Tags = styled.div`
	padding-top: 1rem;
	display: flex;
	justify-content: space-between;
`;
const Tag = styled.span`
	font-weight: 700;
	font-size: 1rem;
	line-height: 1.1rem;
	text-transform: ${(props) => (props.upperCase ? "uppercase" : "normal")};
`;

const Title = styled.h3`
	padding-top: 0.5rem;
	font-size: 2.4rem;
	line-height: 2.8rem;
	width: 250px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;
